import { useState } from 'react';
import { Alert, Button, Card, Descriptions, Typography } from 'antd';
import styled from '@emotion/styled';

import BulkUploadForm from './BulkUploadForm';
import BulkUploadHistory from './BulkUploadHistory';

import useUploadBulkFile from './useUploadBulkFile';
import { formatDateTime } from 'utilities/datesAndTimes';

/** Enough to fix a file by; past this the rest are usually the same mistake. */
const MAX_CSV_ERRORS_SHOWN = 20;

/**
 * Lists a file's errors so they can all be fixed at once. A single error is
 * already the alert's title, so the list only appears for two or more.
 */
const CsvValidationErrorList = ({ errors = [] }) => {
  if (errors.length < 2) {
    return null;
  }
  const hiddenErrorCount = errors.length - MAX_CSV_ERRORS_SHOWN;

  return (
    <ul style={{ margin: 0, paddingInlineStart: 'var(--spacing-md)' }}>
      {errors.slice(0, MAX_CSV_ERRORS_SHOWN).map((error) => (
        <li key={error}>{error}</li>
      ))}
      {hiddenErrorCount > 0 && (
        <li>…and {hiddenErrorCount.toLocaleString()} more.</li>
      )}
    </ul>
  );
};

/** Monospaced so the digest reads as one run rather than wrapping mid-hash. */
const Digest = styled.span`
  font-family: var(--font-family-code, monospace);
`;

const BulkOperationsPage = () => {
  const [uploadFile, uploadState] = useUploadBulkFile();
  const [landed, setLanded] = useState(0);
  const upload = uploadState.data;

  /**
   * Cleared on success, not on dismiss: the submit button is live again the moment
   * the receipt appears. Remounted because the picked file is antd `Upload` state.
   */
  const submit = async (file, options) => {
    const data = await uploadFile(file, options);

    if (data) {
      setLanded((count) => count + 1);
    }
  };

  return (
    <>
      <title>Bulk Operations | Admin</title>

      <Card title="Bulk Operations">
        <Typography.Paragraph type="secondary">
          Upload a CSV of accounts or customers and apply the same actions to
          every row. The file goes straight to storage — it never passes through
          the API.
        </Typography.Paragraph>

        <BulkUploadForm
          key={landed}
          onUpload={submit}
          uploading={uploadState.loading}
        />

        {uploadState.error && (
          <Alert
            showIcon
            type="error"
            title={uploadState.error.message}
            description={
              <CsvValidationErrorList errors={uploadState.error.errors} />
            }
            style={{ marginTop: 'var(--spacing-md)' }}
            data-testid="bulk-upload-error"
          />
        )}

        {upload && (
          <div
            style={{ marginTop: 'var(--spacing-lg)' }}
            data-testid="bulk-upload-result"
          >
            <Alert
              showIcon
              type="success"
              title={`${upload.filename} uploaded and registered`}
              style={{ marginBottom: 'var(--spacing-md)' }}
              action={
                <Button size="small" onClick={uploadState.reset}>
                  Upload another
                </Button>
              }
            />

            <Descriptions
              size="small"
              bordered
              column={{ xs: 1, sm: 2, lg: 3 }}
              items={[
                { key: 'label', label: 'Label', children: upload.label ?? '—' },
                {
                  key: 'rowCount',
                  label: 'Rows',
                  children: upload.rowCount.toLocaleString(),
                },
                {
                  key: 'uniqueIdCount',
                  label: `Unique ${upload.recordType} ids`,
                  children: upload.uniqueIdCount.toLocaleString(),
                },
                {
                  key: 'sizeBytes',
                  label: 'Size',
                  children: `${upload.sizeBytes.toLocaleString()} bytes`,
                },
                {
                  key: 'uploadedTz',
                  label: 'Uploaded',
                  children: formatDateTime(upload.uploadedTz, true),
                },
                {
                  key: 'requesterId',
                  label: 'Uploaded by',
                  children: upload.requesterId,
                },
                {
                  key: 'contentSha256',
                  label: 'SHA-256',
                  span: 3,
                  children: <Digest>{upload.contentSha256}</Digest>,
                },
              ]}
            />
          </div>
        )}
      </Card>

      {/* Remounted on a new upload so the file just sent is in the list. A count
          rather than the token: the token goes back to undefined when the receipt
          is dismissed, which would throw away the agent's filter and page. */}
      <BulkUploadHistory key={landed} />
    </>
  );
};

export default BulkOperationsPage;
